import React from 'react';
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import InteractiveMap from "@/components/InteractiveMap";
import { useLanguage } from "@/contexts/LanguageContext";
import LazyZohoContactForm from "./LazyZohoContactForm";

const Contact: React.FC = () => {
  const { t } = useLanguage();

  const contactInfo = [
    {
      icon: Phone,
      title: t('contact.info.customer_service'),
      details: [t('contact.phone_value'), "+55 (37) 99860-6150"]
    },
    {
      icon: Mail,
      title: t('contact.info.email'),
      details: [t('contact.email_value')]
    },
    {
      icon: MapPin,
      title: t('contact.info.address'),
      details: ["Rua Belo Horizonte, 6 Santo Antônio - Arcos/MG 35599-170"]
    }
  ];

  return (
    <section id="contato" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Lazy Zoho contact form */}
            <div className="flex justify-center">
              <LazyZohoContactForm />
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Contact Details */}
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0" aria-hidden="true">
                      <info.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">
                        {info.title}
                      </h3>
                      <div className="space-y-1">
                        {info.details.map((detail, idx) => {
                          const isPhone = detail.includes('+55') || detail.includes('0800');
                          const isEmail = detail.includes('@');
                          
                          if (isPhone) {
                            return (
                              <a 
                                key={idx} 
                                href={`tel:${detail.replace(/\D/g, '')}`}
                                className="text-muted-foreground hover:text-primary focus:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded px-1 transition-colors"
                                aria-label={`Ligar para ${detail}`}
                              >
                                {detail}
                              </a>
                            );
                          }
                          
                          if (isEmail) {
                            return (
                              <a 
                                key={idx} 
                                href={`mailto:${detail}`}
                                className="text-muted-foreground hover:text-primary focus:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded px-1 transition-colors"
                                aria-label={`Enviar e-mail para ${detail}`}
                              >
                                {detail}
                              </a>
                            );
                          }
                          
                          return (
                            <p key={idx} className="text-muted-foreground">
                              {detail}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp CTA */}
              <Card className="border-0 shadow-medium bg-primary/5">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                    <MessageCircle className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t('contact.quick_support')}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {t('contact.quick_support_description')}
                  </p>
                  <Button 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    onClick={() => window.open('https://wa.me/5537998606150', '_blank')}
                    aria-label="Entrar em contato via WhatsApp - abre em nova janela"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                    {t('contact.whatsapp')}
                  </Button>
                </CardContent>
              </Card>

              {/* Interactive Map */}
              <Card className="border-0 shadow-medium">
                <CardHeader>
                  <CardTitle className="text-lg text-foreground">
                    {t('contact.locations')}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {t('contact.locations_description')}
                  </p>
                </CardHeader>
                <CardContent className="p-0">
                  <InteractiveMap />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;